import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { ImageKeyUploadField } from "@/shared/ui/ImageKeyUploadField";
import { QueryState } from "@/shared/ui/QueryState";
import {
  Button,
  Card,
  cn,
  FormInput,
  FormRoot,
  FormTextarea,
  RadioGroup,
  RadioGroupItem,
  RHF,
  Separator,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useNavigate, useParams, useRouter } from "@tanstack/react-router";
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  ImagePlus,
  Plus,
  Trash2,
  XCircle,
} from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { z } from "zod";
import type { AuditPlanDetailDTO } from "../api/client";
import {
  useAuditPlanDetailQuery,
  useCreateAuditRecordMutation,
} from "../api/queries";

const AUDIT_RECORD_IMAGE_PREFIX = "uploads/internal-audit-record";

function toDatetimeLocalValue(d: Date) {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const formSchema = z.object({
  auditDate: z.string().min(1, "ຕ້ອງລະບຸວັນທີ"),
  auditScope: z.string().min(1, "ຕ້ອງລະບຸຂອບເຂດການຕວດ"),
  summary: z.string().optional(),
  overallResult: z.enum(["pass", "fail", "needs_improvement"]),
  status: z.enum(["draft", "completed"]).default("completed"),
  items: z
    .array(
      z.object({
        title: z.string().min(1, "ຕ້ອງມີຫົວຂໍ້"),
        description: z.string().optional().nullable(),
        result: z.enum(["pass", "fail", "needs_improvement"]),
        notes: z.string().optional().nullable(),
      }),
    )
    .min(1, "ຕ້ອງມີຢ່າງໜ້ອຍ 1 ລາຍການ"),
  attachments: z
    .array(
      z.object({
        key: z.string(),
        file: z.union([z.instanceof(File), z.null()]),
      }),
    )
    .max(10)
    .default([]),
});

type FormValues = z.infer<typeof formSchema>;

const resultOptions: {
  value: FormValues["overallResult"];
  label: string;
  Icon: typeof CheckCircle2;
}[] = [
  { value: "pass", label: "ຜ່ານ", Icon: CheckCircle2 },
  { value: "fail", label: "ບໍ່ຜ່ານ", Icon: XCircle },
  {
    value: "needs_improvement",
    label: "ຕ້ອງປັບປຸງ",
    Icon: AlertTriangle,
  },
];

function resultOptionCardClass(
  value: FormValues["overallResult"],
  selected: boolean,
) {
  const base =
    "flex cursor-pointer items-center gap-3 rounded-xl border px-3.5 py-3.5 text-sm transition-all duration-200 sm:px-4";
  if (!selected) {
    return cn(
      base,
      "border-border/80 bg-background hover:border-primary/30 hover:bg-muted/40",
    );
  }
  if (value === "pass") {
    return cn(
      base,
      "border-primary bg-primary/10 shadow-sm ring-2 ring-primary/25",
    );
  }
  if (value === "fail") {
    return cn(
      base,
      "border-destructive bg-destructive/10 shadow-sm ring-2 ring-destructive/25",
    );
  }
  return cn(
    base,
    "border-amber-500/80 bg-amber-500/10 shadow-sm ring-2 ring-amber-500/25",
  );
}

function resultOptionIconClass(
  value: FormValues["overallResult"],
  selected: boolean,
) {
  if (!selected) return "text-muted-foreground/70";
  if (value === "pass") return "text-primary";
  if (value === "fail") return "text-destructive";
  return "text-amber-800 dark:text-amber-600";
}

function defaultFormValues(): FormValues {
  return {
    auditDate: toDatetimeLocalValue(new Date()),
    auditScope: "",
    summary: "",
    overallResult: "pass",
    status: "completed",
    items: [
      {
        title: "",
        description: "",
        result: "pass",
        notes: "",
      },
    ],
    attachments: [],
  };
}

export function InternalAuditRecordFormPage() {
  const { planId } = useParams({
    from: "/app/internal-audit/plan/$planId",
  });
  const planQuery = useAuditPlanDetailQuery(planId, {
    enabled: Boolean(planId),
  });

  if (planQuery.isLoading) {
    return (
      <>
        <Header />
        <Main className="p-4 md:p-6">
          <QueryState isLoading>ກຳລັງໂຫຼດແຜນ...</QueryState>
        </Main>
      </>
    );
  }

  if (planQuery.isError || !planQuery.data) {
    return (
      <>
        <Header />
        <Main className="p-4 md:p-6">
          <QueryState isError error={planQuery.error}>
            ບໍ່ພົບແຜນ ຫຼື ບໍ່ອະນຸຍາດເຂົ້າເຖິງ
          </QueryState>
        </Main>
      </>
    );
  }

  return <InternalAuditRecordFormInner plan={planQuery.data} planId={planId} />;
}

function InternalAuditRecordFormInner({
  plan,
  planId,
}: {
  plan: AuditPlanDetailDTO;
  planId: string;
}) {
  const router = useRouter();
  const nav = useNavigate();
  const createMutation = useCreateAuditRecordMutation();
  const methods = RHF.useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultFormValues(),
  });

  return (
    <>
      <Header />
      <Main className="flex flex-col gap-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h1 className="font-semibold text-xl md:text-2xl">
            ບັນທຶກການກວດສອບພາຍໃນ
          </h1>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="gap-1"
            onClick={() => router.history.back()}
          >
            <ArrowLeft className="size-4" aria-hidden />
            ກັບ
          </Button>
        </div>

        <Card className="gap-0 border-border p-4 md:p-5">
          <p className="font-medium text-base">{plan.name}</p>
          {plan.description ? (
            <p className="mt-1 text-muted-foreground text-sm">
              {plan.description}
            </p>
          ) : null}
          <p className="mt-2 text-muted-foreground text-xs">
            {plan.auditTypeName} · {plan.departmentName}
            {plan.qcStandardName ? ` · ${plan.qcStandardName}` : ""}
          </p>
        </Card>

        <FormRoot<FormValues>
          key={planId}
          methods={methods}
          onSubmit={async (values) => {
            try {
              const attachmentKeys: string[] = [];
              for (const row of values.attachments) {
                let key = row.key.trim();
                if (row.file instanceof File) {
                  key = await uploadImageToKey(
                    AUDIT_RECORD_IMAGE_PREFIX,
                    row.file,
                  );
                }
                if (key) attachmentKeys.push(key);
              }
              const uniqueAttachmentKeys = [...new Set(attachmentKeys)];

              const res = await createMutation.mutateAsync({
                auditPlanId: planId,
                auditDate: values.auditDate,
                auditScope: values.auditScope,
                summary: values.summary?.trim() || null,
                overallResult: values.overallResult,
                status: values.status,
                items: values.items.map((it) => ({
                  title: it.title.trim(),
                  description: it.description?.trim() || null,
                  result: it.result,
                  notes: it.notes?.trim() || null,
                })),
                attachmentKeys:
                  uniqueAttachmentKeys.length > 0
                    ? uniqueAttachmentKeys
                    : undefined,
              });
              toast.success("ບັນທຶກສຳເລັດ");
              await nav({
                to: "/app/internal-audit/record/$recordId",
                params: { recordId: res.recordId },
              });
            } catch {
              /* toast ຈາກ fetcher / mutation */
            }
          }}
        >
          <InternalAuditFormFields disabled={createMutation.isPending} />
        </FormRoot>
      </Main>
    </>
  );
}

function InternalAuditFormFields({ disabled }: { disabled: boolean }) {
  const { control } = RHF.useFormContext<FormValues>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "items",
  });
  const {
    fields: attachmentFields,
    append: appendAttachment,
    remove: removeAttachment,
  } = useFieldArray({
    control,
    name: "attachments",
  });

  return (
    <div className="space-y-6">
      <Card className="gap-0 border-border p-4 md:p-5">
        <h2 className="mb-4 font-medium text-base">ຂໍ້ມູນທົ່ວໄປ</h2>
        <div className="space-y-4">
          <FormInput
            label="ວັນທີເວລາກວດ"
            name="auditDate"
            type="datetime-local"
            requiredMark
            disabled={disabled}
          />
          <FormTextarea
            label="ຂອບເຂດການຕວດ"
            name="auditScope"
            rows={3}
            placeholder="ສິ່ງທີ່ກວດ, ພື້ນທີ່, ກະບວນການ..."
            requiredMark
            disabled={disabled}
          />
          <FormTextarea
            label="ສະຫຼຸບ (ທາງເລືອກ)"
            name="summary"
            rows={2}
            disabled={disabled}
          />
          <RHF.Controller
            name="overallResult"
            render={({ field }) => (
              <fieldset className="space-y-2">
                <legend className="font-medium text-sm">ຜົນລວມ</legend>
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="grid gap-3 sm:grid-cols-3"
                  disabled={disabled}
                >
                  {resultOptions.map((o) => {
                    const selected = field.value === o.value;
                    return (
                      <label
                        key={o.value}
                        htmlFor={`overall-${o.value}`}
                        className={resultOptionCardClass(o.value, selected)}
                      >
                        <o.Icon
                          className={cn(
                            "size-5 shrink-0",
                            resultOptionIconClass(o.value, selected),
                          )}
                          aria-hidden
                        />
                        <span className="min-w-0 flex-1 font-semibold leading-snug">
                          {o.label}
                        </span>
                        <RadioGroupItem
                          value={o.value}
                          id={`overall-${o.value}`}
                          className="shrink-0"
                        />
                      </label>
                    );
                  })}
                </RadioGroup>
              </fieldset>
            )}
          />
        </div>
      </Card>

      <Card className="gap-0 border-border p-4 md:p-5">
        <h2 className="mb-1 font-medium text-base">ຮູບປະກອບ (ທາງເລືອກ)</h2>
        <p className="mb-4 text-muted-foreground text-sm">
          ບໍ່ແນບຮູບກໍ່ບັນທຶກໄດ້ — ແນບໄດ້ສູງສຸດ 10 ຮູບ, ອັບໂຫຼດຕອນກົດບັນທຶກ
        </p>
        {attachmentFields.length === 0 ? (
          <p className="text-muted-foreground text-sm">ຍັງບໍ່ມີຮູບ</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            {attachmentFields.map((af, imgIndex) => (
              <div
                key={af.id}
                className="flex flex-col gap-3 rounded-xl border border-border/70 bg-muted/20 p-3 sm:flex-row sm:items-start"
              >
                <div className="min-w-0 flex-1">
                  <RHF.Controller
                    name={`attachments.${imgIndex}.key`}
                    render={({ field }) => (
                      <RHF.Controller
                        name={`attachments.${imgIndex}.file`}
                        render={({ field: fileField }) => (
                          <ImageKeyUploadField
                            label={`ຮູບ ${imgIndex + 1}`}
                            value={field.value ?? ""}
                            onChange={field.onChange}
                            deferUpload
                            pendingFile={fileField.value}
                            onFileSelect={(f) => fileField.onChange(f)}
                            onClear={() => {
                              field.onChange("");
                              fileField.onChange(null);
                            }}
                            keyPrefix={AUDIT_RECORD_IMAGE_PREFIX}
                            aspectRatio="aspect-video"
                            disabled={disabled}
                          />
                        )}
                      />
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="size-9 shrink-0 self-end sm:self-start"
                  disabled={disabled}
                  onClick={() => removeAttachment(imgIndex)}
                  aria-label="ລຶບຮູບນີ້"
                >
                  <Trash2 className="size-4" />
                </Button>
              </div>
            ))}
          </div>
        )}
        <div className="mt-4 flex justify-end">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={disabled || attachmentFields.length >= 10}
            onClick={() => appendAttachment({ key: "", file: null })}
          >
            <ImagePlus className="size-4" aria-hidden />
            ເພີ່ມຮູບ
          </Button>
        </div>
      </Card>

      <div className="space-y-3">
        <h2 className="font-medium text-base">ລາຍການກວດ</h2>

        {fields.map((field, index) => (
          <Card
            key={field.id}
            className="gap-0 overflow-hidden border-border/80 py-0 shadow-sm transition-shadow duration-200 hover:shadow-md"
          >
            <div className="flex gap-4 px-4 pt-4 pb-3 md:px-5">
              <div
                className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-muted font-semibold text-muted-foreground text-sm tabular-nums"
                aria-hidden
              >
                {index + 1}
              </div>
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <h3 className="font-semibold text-foreground text-sm leading-snug tracking-tight">
                    ລາຍການກວດ
                  </h3>
                  {fields.length > 1 ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="size-8 shrink-0 text-destructive hover:bg-destructive/10"
                      disabled={disabled}
                      onClick={() => remove(index)}
                      aria-label="ລຶບລາຍການ"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  ) : null}
                </div>
                <FormInput
                  label="ຫົວຂໍ້"
                  name={`items.${index}.title`}
                  requiredMark
                  placeholder="ຫົວຂໍ້ກວດ"
                  disabled={disabled}
                />
                <FormTextarea
                  label="ລາຍລະອຽດ (ທາງເລືອກ)"
                  name={`items.${index}.description`}
                  rows={2}
                  placeholder="ລາຍລະອຽດກວດ"
                  disabled={disabled}
                />
              </div>
            </div>

            <Separator className="bg-border/70" />

            <RHF.Controller
              name={`items.${index}.result`}
              render={({ field: resultField }) => (
                <div className="space-y-3 bg-muted/15 px-4 py-4 md:px-5">
                  <p className="font-medium text-[0.7rem] text-muted-foreground uppercase tracking-wider">
                    ຜົນລາຍການ
                  </p>
                  <RadioGroup
                    value={resultField.value}
                    onValueChange={resultField.onChange}
                    disabled={disabled}
                    className="grid gap-3 sm:grid-cols-3"
                  >
                    {resultOptions.map((o) => {
                      const selected = resultField.value === o.value;
                      const inputId = `item-${index}-result-${o.value}`;
                      return (
                        <label
                          key={o.value}
                          htmlFor={inputId}
                          className={resultOptionCardClass(o.value, selected)}
                        >
                          <o.Icon
                            className={cn(
                              "size-5 shrink-0",
                              resultOptionIconClass(o.value, selected),
                            )}
                            aria-hidden
                          />
                          <span className="min-w-0 flex-1 font-semibold leading-snug">
                            {o.label}
                          </span>
                          <RadioGroupItem
                            value={o.value}
                            id={inputId}
                            className="shrink-0"
                          />
                        </label>
                      );
                    })}
                  </RadioGroup>
                </div>
              )}
            />

            <Separator className="bg-border/70" />

            <div className="px-4 py-4 md:px-5">
              <FormTextarea
                placeholder="ໝາຍເຫດກວດ"
                label="ໝາຍເຫດ (ທາງເລືອກ)"
                name={`items.${index}.notes`}
                rows={2}
                disabled={disabled}
              />
            </div>
          </Card>
        ))}

        <div className="flex justify-end pt-1">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="gap-1"
            disabled={disabled}
            onClick={() =>
              append({
                title: "",
                description: "",
                result: "pass",
                notes: "",
              })
            }
          >
            <Plus className="size-4" aria-hidden />
            ເພີ່ມລາຍການ
          </Button>
        </div>
      </div>

      <Separator />

      <div className="fixed inset-x-0 bottom-0 z-10 border-t bg-background/95 px-4 py-3 backdrop-blur md:static md:border-0 md:bg-transparent md:px-0 md:py-0">
        <div className="mx-auto flex max-w-3xl justify-end gap-2 md:max-w-none">
          <Button type="submit" disabled={disabled} className="min-w-32">
            {disabled ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກ"}
          </Button>
        </div>
      </div>
    </div>
  );
}
